import Vue from 'vue'
import moment from 'moment'
import axios from 'axios'
import _ from 'lodash'

const state = () => ({
  champions: {},
  items: {},
  traits: {},
  championKeyMap: {},
  itemKeyMap: {},
  championTraitMap: {},
  setData: {},
  setDataExpiry: null,
})

const mutations = {
  'UPDATE_CHAMPIONS': (state, championArray) => {
    // set or update champion in state
    championArray.forEach((champion) => {
      Vue.set(state.champions, champion.id, champion)
    })
  },
  'UPDATE_TRAITS': (state, traitArray) => {
    // set or update trait in state
    traitArray.forEach((trait) => {
      Vue.set(state.traits, trait.id, trait)
    })
  },
  'UPDATE_ITEMS': (state, itemArray) => {
    // set or update item in state
    itemArray.forEach((item) => {
      Vue.set(state.items, item.id, item)
    })
  },
  'UPDATE_CHAMPION_KEY_MAP': (state, championArray) => {
    // Create map of champion keys to array of champion ids
    // One champion key can now reference other versions of the champion
    championArray.forEach((champion) => {
      // set or update map
      let currentMap = state.championKeyMap[champion.key]
      if (currentMap) {
        if (!currentMap.includes(champion.id)) {
          state.championKeyMap[champion.key].push(champion.id)
        }
      } else {
        Vue.set(state.championKeyMap, champion.key, [champion.id])
      }
    })
  },
  'UPDATE_ITEM_KEY_MAP': (state, itemArray) => {
    // Create map of item keys to array of item ids
    // One item key can now reference other versions of the item
    itemArray.forEach((item) => {
      // set or update map
      let currentMap = state.itemKeyMap[item.key]
      if (currentMap) {
        if (!currentMap.includes(item.id)) {
          state.itemKeyMap[item.key].push(item.id)
        }
      } else {
        Vue.set(state.itemKeyMap, item.key, [item.id])
      }
    })
  },
  'UPDATE_CHAMPION_TRAIT_MAP': (state, championArray) => {
    // Create map of trait ids to an array of champion ids
    championArray.forEach((champion) => {
      let traits = champion.classes.concat(champion.origins)
      traits.forEach((trait) => {
        // set or update map
        let currentMap = state.championTraitMap[trait.id]
        if (currentMap) {
          if (!currentMap.includes(champion.id)) {
            state.championTraitMap[trait.id].push(champion.id)
          }
        } else {
          Vue.set(state.championTraitMap, trait.id, [champion.id])
        }
      })
    })
  },
  'UPDATE_SET_DATA': (state, payload) => {
    // updated_at is an object with different timestamp for each data type
    // only set updated_at if a data type which has been updated is passed in
    let set = payload.setData
    if (state.setData[set.id]) {
      Vue.set(state.setData[set.id], 'patch', set.patch)
      if (payload.dataType) {
        Vue.set(state.setData[set.id].dataUpdatedAt, payload.dataType, set.updated_at)
      }
    } else {
      let setDataObj = {
        id: set.id,
        patch: set.patch,
        dataUpdatedAt: {},
        updated_at: set.updated_at,
      }
      if (payload.dataType) {
        Vue.set(setDataObj.dataUpdatedAt, payload.dataType, set.dataUpdatedAt)
      }
      Vue.set(state.setData, set.id, setDataObj)
    }
  },
  'UPDATE_SET_DATA_EXPIRY': (state, expiry) => {
    state.setDataExpiry = expiry
  }
}

const actions = {
  async fetchSetData ({commit, state}) {
    // check if set data has been fetched recently
    if (moment(state.setDataExpiry) > moment()) {
      return
    }

    let setData = await ajax.sets()
    setData.forEach((set) => {
      commit('UPDATE_SET_DATA', {
        setData: set
      })
    })

    let setDataExpiry = moment().add(5, 'minutes').format()
    commit('UPDATE_SET_DATA_EXPIRY', setDataExpiry)
    return
  },
  async fetchChampionByKey ({commit}, key) {
    let championVersions = await ajax.championByKey(key)
    commit('UPDATE_CHAMPIONS', championVersions)
    commit('UPDATE_CHAMPION_KEY_MAP', championVersions)
    return
  },
  async fetchRecentSetChampions ({state, dispatch}) {
    await dispatch('fetchSetData')

    let sortedSetData = Object.keys(state.setData).sort((a, b) => {
      if (a > b) {
        return 1
      } else if (a < b) {
        return -1
      } else {
        return 0
      }
    })

    await dispatch('fetchChampionsBySet', _.last(sortedSetData))
  },
  async fetchChampionsBySet ({commit, state, dispatch}, setNumber) {
    let oldSetTime = _.get(state, `setData.${setNumber}.dataUpdatedAt.champions`)

    await dispatch('fetchSetData')
    let currentSetData = _.get(state, 'setData.' + setNumber)

    if (oldSetTime) {
      oldSetTime = moment(oldSetTime)

      // if there is no timestamp for this set/data-type then pass on and fetch data
      let newSetTimestamp = _.get(currentSetData, `dataUpdatedAt.champions`)
      if (newSetTimestamp) {
        let newSetTime = moment(newSetTimestamp)
        // if updated_at on server is the same as the one locally, dont need to fetch
        if (newSetTime.isSame(oldSetTime)) {
          return
        }
      }
    }

    let champions = await ajax.champions(setNumber)
    commit('UPDATE_CHAMPIONS', champions)
    commit('UPDATE_CHAMPION_TRAIT_MAP', champions)
    commit('UPDATE_CHAMPION_KEY_MAP', champions)
    commit('UPDATE_SET_DATA', {
      setData: currentSetData,
      dataType: 'champions'
    })
  },
  async fetchRecentSetTraits ({state, dispatch}) {
    await dispatch('fetchSetData')

    let sortedSetData = Object.keys(state.setData).sort((a, b) => {
      if (a > b) {
        return 1
      } else if (a < b) {
        return -1
      } else {
        return 0
      }
    })

    await dispatch('fetchTraitsBySet', _.last(sortedSetData))
  },
  async fetchTraitsBySet ({commit, state, dispatch}, setNumber) {
    let oldSetTime = _.get(state, `setData.${setNumber}.dataUpdatedAt.traits`)

    await dispatch('fetchSetData')
    let currentSetData = _.get(state, 'setData.' + setNumber)

    if (oldSetTime) {
      oldSetTime = moment(oldSetTime)

      // if there is no timestamp for this set/data-type then pass on and fetch data
      let newSetTimestamp = _.get(currentSetData, `dataUpdatedAt.traits`)
      if (newSetTimestamp) {
        let newSetTime = moment(newSetTimestamp)
        // if updated_at on server is the same as the one locally, dont need to fetch
        if (newSetTime.isSame(oldSetTime)) {
          return
        }
      }
    }

    let traits = await ajax.traits(setNumber)
    commit('UPDATE_TRAITS', traits)
    commit('UPDATE_SET_DATA', {
      setData: currentSetData,
      dataType: 'traits'
    })
  },
  async fetchRecentSetItems ({state, dispatch}) {
    await dispatch('fetchSetData')

    let sortedSetData = Object.keys(state.setData).sort((a, b) => {
      if (a > b) {
        return 1
      } else if (a < b) {
        return -1
      } else {
        return 0
      }
    })

    await dispatch('fetchItemsBySet', _.last(sortedSetData))
  },
  async fetchItemsBySet ({commit, state, dispatch}, setNumber) {
    let oldSetTime = _.get(state, `setData.${setNumber}.dataUpdatedAt.items`)

    await dispatch('fetchSetData')
    let currentSetData = _.get(state, 'setData.' + setNumber)

    if (oldSetTime) {
      oldSetTime = moment(oldSetTime)

      // if there is no timestamp for this set/data-type then pass on and fetch data
      let newSetTimestamp = _.get(currentSetData, `dataUpdatedAt.items`)
      if (newSetTimestamp) {
        let newSetTime = moment(newSetTimestamp)
        // if updated_at on server is the same as the one locally, dont need to fetch
        if (newSetTime.isSame(oldSetTime)) {
          return
        }
      }
    }

    let items = await ajax.items(setNumber)
    commit('UPDATE_ITEMS', items)
    commit('UPDATE_ITEM_KEY_MAP', items)
    commit('UPDATE_SET_DATA', {
      setData: currentSetData,
      dataType: 'items'
    })
  },
}

const getters = {
  getChampionTraitMap: state => { return state.championTraitMap },
  getChampionKeyMap: state => { return state.championKeyMap },
  getChampions: state => { return state.champions },
  getChampionsBySet: state => set => {
    return Object.values(state.champions).filter((champion) => {
      return champion.set_id == set
    })
  },
  getRecentSetChampions: (state, getters) => {
    return getters.getChampionsBySet(getters.getLatestSet)
  },
  getChampionByKey: (state, getters) => key => {
    let championSetMap = {}
    let championIds = _.get(getters, 'getChampionKeyMap.' + key, [])
    championIds.forEach((id) => {
      let champion = getters.getChampions[id]
      if (champion) {
        championSetMap[champion.set_id] = champion
      }
    })
    return championSetMap
  },
  getItems: state => { return state.items },
  getItemKeyMap: state => { return state.itemKeyMap },
  getItemsBySet: state => set => {
    return Object.values(state.items).filter((item) => {
      return item.set_id == set
    })
  },
  getItemByKey: (state, getters) => key => {
    let itemSetMap = {}
    let itemIds = _.get(getters, 'getItemKeyMap.' + key, [])
    itemIds.forEach((id) => {
      let item = getters.getItems[id]
      if (item) {
        itemSetMap[item.set_id] = item
      }
    })
    return itemSetMap
  },
  getRecentSetItems: (state, getters) => {
    return getters.getItemsBySet(getters.getLatestSet)
  },
  getTraits: state => { return state.traits },
  getOrigins: state => {
    return Object.values(state.traits).filter((trait) => {
      return trait.type === 'champion-origin'
    })
  },
  getOriginsBySet: (state, getters) => set => {
    return getters.getOrigins.filter(origin => origin.set_id == set)
  },
  getClasses: state => {
    return Object.values(state.traits).filter((trait) => {
      return trait.type === 'champion-class'
    })
  },
  getClassesBySet: (state, getters) => set => {
    return getters.getClasses.filter(championClass => championClass.set_id == set)
  },
  getSets: state => {
    return state.setData
  },
  getLatestSet: state => {
    let setNumbers = Object.keys(_.get(state, 'setData'))
    return _.last(setNumbers.sort())
  },
  getCurrentPatch: (state, getters) => {
    return _.get(state, `setData.${getters.getLatestSet}.patch`)
  },
  getSetData: state => { return state.setData }
}

// Ajax calls to be used in actions
const ajax = {
  sets () {
    return axios({
      method: 'get',
      url: process.env.PROGUIDES_WEB_URL + 'api/v2/game-resources/teamfight-tactics/static-data/sets'
    }).then((response) => {
      return response.data
    }).catch((error) => {
      return Promise.reject(error)
    })
  },
  champions (setNumber) {
    return axios({
      method: 'get',
      url: process.env.PROGUIDES_WEB_URL + 'api/v2/game-resources/teamfight-tactics/static-data/champions' + `${setNumber ? `?set=${setNumber}` : ''}`
    }).then((response) => {
      return response.data
    }).catch((error) => {
      return Promise.reject(error)
    })
  },
  championByKey (key) {
    return axios({
      method: 'get',
      url: process.env.PROGUIDES_WEB_URL + 'api/v2/game-resources/teamfight-tactics/static-data/champions/by-key/' + key
    }).then((response) => {
      return response.data
    }).catch((error) => {
      return Promise.reject(error)
    })
  },
  championById (id) {
    return axios({
      method: 'get',
      url: process.env.PROGUIDES_WEB_URL + 'api/v2/game-resources/teamfight-tactics/static-data/champions/by-id/' + id
    }).then((response) => {
      return response.data
    }).catch((error) => {
      return Promise.reject(error)
    })
  },
  traits (setNumber) {
    return axios({
      method: 'get',
      url: process.env.PROGUIDES_WEB_URL + 'api/v2/game-resources/teamfight-tactics/static-data/traits' + `${setNumber ? `?set=${setNumber}` : ''}`
    }).then((response) => {
      return response.data
    }).catch((error) => {
      return Promise.reject(error)
    })
  },
  traitByKey (key) {
    return axios({
      method: 'get',
      url: process.env.PROGUIDES_WEB_URL + 'api/v2/game-resources/teamfight-tactics/static-data/traits/by-key/' + key
    }).then((response) => {
      return response.data
    }).catch((error) => {
      return Promise.reject(error)
    })
  },
  traitById (id) {
    return axios({
      method: 'get',
      url: process.env.PROGUIDES_WEB_URL + 'api/v2/game-resources/teamfight-tactics/static-data/traits/by-id/' + id
    }).then((response) => {
      return response.data
    }).catch((error) => {
      return Promise.reject(error)
    })
  },
  items (setNumber) {
    return axios({
      method: 'get',
      url: process.env.PROGUIDES_WEB_URL + 'api/v2/game-resources/teamfight-tactics/static-data/items' + `${setNumber ? `?set=${setNumber}` : ''}`
    }).then((response) => {
      return response.data
    }).catch((error) => {
      return Promise.reject(error)
    })
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/teamfight_tactics.js